import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../../../environments/environment';
import { StateService } from './state.service';
import { Router } from '@angular/router';
import { catchError, map, mergeMap, Observable, of, tap } from 'rxjs';
import { ErrorService } from './error.service';
import { User } from '@cosmos/shared';
import { PostHogService } from './posthog.service';
import { SupportChatService } from './support-chat.service';

@Injectable({ providedIn: 'root' })
export class AuthService {

  constructor(
    private http: HttpClient,
    private stateService: StateService,
    private router: Router,
    private errorService: ErrorService,
    private postHogService: PostHogService,
    private supportChatService: SupportChatService,
  ) {}

  /**
   * Intentional sign-out: calls the server logout endpoint to clear HttpOnly cookies,
   * then purges client state and does a FULL page load to /login.
   *
   * The hard reload (rather than router.navigate) is deliberate: posthog.reset()
   * in _clearClientState() tears down the PostHog Conversations extension and it
   * cannot be re-armed without a fresh posthog.init() — which only happens on a
   * page load. Without this, in-app support chat is dead after a logout→login
   * within the same page session. Logout is rare and deliberate, so a page load
   * here is an acceptable cost.
   */
  signOut(): void {
    this.http
      .post(`${environment.API_URL}/auth/logout`, {}, { withCredentials: true })
      .pipe(catchError(() => of(null)))
      .subscribe(() => {
        this._clearClientState();
        window.location.assign('/login');
      });
  }

  /**
   * Purges client-side auth state WITHOUT calling the server.
   * Used by the app initializer when the session check fails (401 on startup).
   * Also called by ErrorService on 401 mid-session (the cookie is already expired/invalid).
   *
   * Does NOT navigate — startup callers run before the router resolves. For a
   * mid-session forced logout that must land on a page, use forceLogoutReload().
   */
  purgeAuth(): void {
    this._clearClientState();
  }

  /**
   * Mid-session forced logout (e.g. an authorization guard rejecting a route):
   * purge client state, then do a FULL page load to `target`.
   *
   * The hard reload is required for the same reason as signOut(): _clearClientState()
   * runs posthog.reset(), which tears down the PostHog Conversations extension
   * with no client-side re-init path — only a fresh posthog.init() on page load
   * recovers it.
   *
   * @param target absolute path incl. any query string, e.g. '/login?error=role'
   */
  forceLogoutReload(target: string): void {
    this._clearClientState();
    window.location.assign(target);
  }

  private _clearClientState(): void {
    this.stateService.setCurrentUser(null);
    this.postHogService.capture('user session end');
    this.postHogService.reset();
    this.supportChatService.clearIdentity();
  }

  /**
   * Validates the current session by calling GET /users/current.
   * The browser sends the cosmos_session cookie automatically.
   * Called once on app startup by the AppInitializer.
   */
  updateCurrentUser(): Observable<boolean> {
    return this.http
      .get<User>(`${environment.API_URL}/users/current`, {
        observe: 'response',
        withCredentials: true,
      })
      .pipe(
        map(response => {
          if (response.body) {
            this.stateService.setCurrentUser(response.body);
            this.postHogService.identify(response.body._id, {
              email: response.body.email,
              name: response.body.name,
              roles: response.body.roles,
            });
            this.postHogService.capture('user session start');
            this.supportChatService.verifyIdentity();
            return true;
          } else {
            this.purgeAuth();
            return false;
          }
        }),
        // No errorService.handleError here: a 401 from this startup identity
        // check just means "anonymous visitor" — expected on public routes
        // like /reset, /login, /recovery. The app initializer's own .catch()
        // handles purging state; redirecting to /login here would hijack the
        // initial navigation before the router even resolves the real route.
      );
  }

  /**
   * Authenticates the user. On success, the server sets the HttpOnly cosmos_session
   * cookie and returns the UserDto. The client stores the user in state directly —
   * no second GET /users/current round-trip needed.
   */
  login(email: string, password: string): Observable<boolean> {
    return this.http
      .post<any>(
        `${environment.API_URL}/auth/email/login`,
        { email, password },
        { observe: 'response', withCredentials: true }
      )
      .pipe(
        map(response => {
          const user = response.body?.data;
          if (user) {
            this.stateService.setCurrentUser(user);
            this.postHogService.identify(user._id, {
              email: user.email,
              name: user.name,
              roles: user.roles,
            });
            this.postHogService.capture('user session start');
            this.supportChatService.verifyIdentity();
            return true;
          } else {
            this.purgeAuth();
            return false;
          }
        }),
      );
  }

  /**
   * Superadmin starts a "view as" impersonation session. The server swaps the
   * cosmos_session cookie for the target user's and stashes the superadmin's
   * original session in cosmos_impersonator_session.
   */
  viewAs(userId: string): Observable<{ user: User; landingApp: 'f-admin' | 'f-user' }> {
    return this.http
      .post<any>(`${environment.API_URL}/auth/view-as/${userId}`, {}, { observe: 'response', withCredentials: true })
      .pipe(
        map(response => {
          const data = response.body?.data;
          if (data?.user) {
            this.stateService.setCurrentUser(data.user);
          }
          return data;
        }),
        catchError(error => { this.errorService.handleError(error); throw error; }),
      );
  }

  /**
   * Ends the current "view as" session, restoring the superadmin's original
   * cosmos_session from cosmos_impersonator_session.
   */
  exitViewAs(): Observable<User> {
    return this.http
      .post<any>(`${environment.API_URL}/auth/view-as/exit`, {}, { observe: 'response', withCredentials: true })
      .pipe(
        map(response => {
          const user = response.body?.data;
          if (user) {
            this.stateService.setCurrentUser(user);
          }
          return user;
        }),
        catchError(error => { this.errorService.handleError(error); throw error; }),
      );
  }

  signUpSim(email: string, password: string, name: string, surname: string, company_text: string, phone: string, terms: boolean) {
    return this.http
      .post<any>(`${environment.API_URL}/auth/email/signup/simulator`, { email, password, name, surname, company_text, phone, terms}, {observe: 'response'})
        .pipe(
          map(response => {
            return response;
          }),
          catchError( (error) =>{this.errorService.handleError(error); throw error;})
        );
  }

  /**
   * Registers a free-tier account. On success, the server sets the same
   * HttpOnly cosmos_session cookie as /email/login and returns the UserDto —
   * the client stores it directly so the user lands on /validate already
   * logged in, without a separate /login step.
   */
  registerFree(payload: {
    name: string; surname: string; email: string; password: string;
    companyName: string; jobTitle: string; phone: string;
    terms: boolean; marketingConsent: boolean;
  }) {
    return this.http
      .post<any>(`${environment.API_URL}/auth/register-free`, payload, { observe: 'response', withCredentials: true })
      .pipe(
        map(response => {
          const user = response.body?.data;
          if (user) {
            this.stateService.setCurrentUser(user);
          }
          return response.body;
        }),
        catchError(error => { this.errorService.handleError(error); throw error; }),
      );
  }

  verifyPhoneOtp(userId: string, code: string) {
    return this.http
      .post<any>(`${environment.API_URL}/auth/verify-phone-otp`, { userId, code }, { observe: 'response', withCredentials: true })
      .pipe(
        map(response => response.body),
        catchError(error => { this.errorService.handleError(error); throw error; }),
      );
  }

  resendPhoneOtp(userId: string) {
    return this.http
      .post<any>(`${environment.API_URL}/auth/resend-phone-otp`, { userId }, { observe: 'response' })
      .pipe(
        map(response => response.body),
        catchError(error => { this.errorService.handleError(error); throw error; }),
      );
  }

  resendVerificationEmail(userId: string) {
    return this.http
      .post<any>(`${environment.API_URL}/auth/resend-verification-email`, { userId }, { observe: 'response' })
      .pipe(
        map(response => response.body),
        catchError(error => { this.errorService.handleError(error); throw error; }),
      );
  }
}
